import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { pricingPlans } from '../mock';
import { Check, Star } from 'lucide-react';

const PricingSection = ({ onBookDemo }) => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const getDisplayPrice = (plan) => {
    if (plan.monthlyPrice == null) {
      return { price: plan.price, period: plan.period, subtitle: null };
    }
    if (billingPeriod === 'annual') {
      return {
        price: plan.annualPrice,
        period: `(${plan.annualPerMonth})`,
        subtitle: plan.annualSavings,
      };
    }
    return { price: plan.price, period: plan.period, subtitle: null };
  };

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            Start with a 14-day free trial. No credit card required. 
            Scale as your team grows.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex justify-center mb-12">
          <Tabs value={billingPeriod} onValueChange={setBillingPeriod} className="w-auto">
            <TabsList className="bg-slate-200">
              <TabsTrigger value="monthly" className="px-8">Monthly</TabsTrigger>
              <TabsTrigger value="annual" className="px-8">
                Annual
                <Badge className="ml-2 bg-teal-600 text-white">Save 17%</Badge>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {pricingPlans.map((plan, idx) => {
            const display = getDisplayPrice(plan);
            return (
            <Card 
              key={idx}
              className={`p-8 relative ${
                plan.popular 
                  ? 'border-2 border-blue-600 shadow-2xl scale-105 bg-white' 
                  : 'border-2 border-slate-200 bg-white'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white px-4 py-2 shadow-lg">
                    <Star className="w-3 h-3 mr-1 inline fill-current" />
                    Most Popular
                  </Badge>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-600 mb-1">
                  {plan.targetCustomer}
                </p>
                <p className="text-sm text-slate-500 mb-6">
                  {plan.description}
                </p>
                <div className="mb-2">
                  <span className="text-5xl font-bold text-slate-900">
                    {display.price}
                  </span>
                  {display.period && (
                    <span className="text-slate-600 ml-2">
                      {display.period}
                    </span>
                  )}
                </div>
                {display.subtitle && (
                  <p className="text-sm font-medium text-teal-600">{display.subtitle}</p>
                )}
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, featureIdx) => (
                  <li key={featureIdx} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className={`w-full py-6 font-semibold ${
                  plan.popular
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                }`}
                onClick={onBookDemo}
              >
                {plan.cta}
              </Button>
            </Card>
          );
          })}
        </div>

        {/* Trust indicators */}
        <div className="text-center space-y-4">
          <p className="text-slate-600">
            All plans include 14-day free trial • Cancel anytime • No credit card required
          </p>
          <div className="flex justify-center gap-8 text-sm text-slate-500">
            <span>✓ SOC 2 Type II Certified</span>
            <span>✓ GDPR Compliant</span>
            <span>✓ 99.9% Uptime SLA</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
